import { Button, HStack, Heading, Stack } from '@chakra-ui/react';
import { Link } from '@tanstack/react-router';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { SectionHeadline } from '$/components/core/Collection/SectionHeadline';
import { NewProjectButton } from '$/components/core/Projects/components/NewProjectButton';
import { DashboardSectionRow } from '$/pages/DashboardPages/components/DashboardSectionRow';
import { ProjectFolderItem } from '$/pages/DashboardPages/pages/Projects/components/ProjectFolderItem';
import { ProjectGridItems } from '$/pages/DashboardPages/pages/Projects/components/ProjectGridItems';
import { type ProjectFolder } from '$/services/usecases/projects/mapper/editorProject';
import { type ProjectOverviewItem } from '$/services/usecases/projects/mapper/projectOverview';
import { type SchoolClass } from '$/services/usecases/school/mapper/schoolClasses';

interface Props {
  projects: ProjectOverviewItem[];
  folders: ProjectFolder[];
  setSelectedFolderId: (folderId: string | null) => void;
  schoolClass: SchoolClass;
}

export const ProjectOverview: FC<Props> = ({
  folders,
  projects,
  setSelectedFolderId,
  schoolClass,
}) => {
  const { t } = useTranslation();
  const user = useAuthenticationStore.useUser();
  return (
    <Stack gap='6' overflowY='auto' w='full' p='7'>
      <HStack>
        <Button
          as={Link}
          px='0'
          leftIcon={<Icon icon='arrow_right' transform='rotate(180deg)' />}
          onClick={() => setSelectedFolderId(null)}
          to='/classes'
          variant='text'
        >
          {t('general.backToOverview')}
        </Button>
      </HStack>
      <HStack justify='space-between'>
        <Heading as='h1' mt='2' mb='4' fontSize='3xl'>
          {schoolClass.name}
        </Heading>
        <NewProjectButton schoolClassId={schoolClass.id} />
      </HStack>
      {folders.length > 0 && (
        <SectionHeadline headline={t('dashboard.projects.folders')}>
          <DashboardSectionRow>
            {folders.map((folder) => (
              <ProjectFolderItem
                projectFolder={folder}
                key={folder.id}
                icon={folder.userId === user?.id ? 'folder' : 'account'}
                onClick={() => setSelectedFolderId(folder.id)}
              />
            ))}
          </DashboardSectionRow>
        </SectionHeadline>
      )}
      {projects.length > 0 && (
        <SectionHeadline headline={t('dashboard.projects.title')}>
          <DashboardSectionRow>
            <ProjectGridItems items={projects} hideNewProjectButton={true} />
          </DashboardSectionRow>
        </SectionHeadline>
      )}
    </Stack>
  );
};
